import { PersonalSkillDraftSchema } from '@hector/shared-types';
import type {
  PersonalSkillDraft,
  ResolvedSkillMetadata,
  SkillSettingsEntry,
} from '@hector/shared-types';

export interface SlashToken {
  start: number;
  end: number;
  query: string;
}

function searchable(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr');
}

export function normalizeSkillSlugInput(value: string): string {
  return searchable(value)
    .replace(/^\/+/, '')
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function validateSkillDraft(
  draft: PersonalSkillDraft,
): Partial<Record<keyof PersonalSkillDraft, string>> {
  const parsed = PersonalSkillDraftSchema.safeParse(draft);
  if (parsed.success) return {};
  const errors: Partial<Record<keyof PersonalSkillDraft, string>> = {};
  for (const issue of parsed.error.issues) {
    const field = issue.path[0];
    if (field === 'slug') {
      errors.slug = 'Utilisez des lettres minuscules, des chiffres et des tirets.';
    } else if (field === 'description') {
      errors.description = 'Décrivez quand Hector doit utiliser cette compétence.';
    } else if (field === 'content') {
      errors.content = 'Ajoutez les instructions de la compétence.';
    }
  }
  return errors;
}

export function getSlashToken(value: string, cursor: number): SlashToken | null {
  const boundedCursor = Math.max(0, Math.min(cursor, value.length));
  const beforeCursor = value.slice(0, boundedCursor);
  const match = beforeCursor.match(/(?:^|\s)\/([^\s/]*)$/);
  if (!match) return null;
  const query = match[1] ?? '';
  return {
    start: boundedCursor - query.length - 1,
    end: boundedCursor,
    query,
  };
}

export function filterSkills(
  skills: ResolvedSkillMetadata[],
  query: string,
): ResolvedSkillMetadata[] {
  const needle = searchable(query.trim());
  return skills.filter((skill) => {
    if (!skill.isActive || skill.availability !== 'active') return false;
    if (!needle) return true;
    return searchable(`${skill.name} ${skill.slug} ${skill.description}`).includes(needle);
  });
}

const CUSTOMIZATION_ORDER = {
  overridden: 0,
  personal: 1,
  hector: 2,
} as const;

/** Stable palette order shared by rendering and keyboard navigation. */
export function sortSkillsForPalette(
  skills: ResolvedSkillMetadata[],
): ResolvedSkillMetadata[] {
  return [...skills].sort((left, right) => {
    const customization = CUSTOMIZATION_ORDER[left.customization]
      - CUSTOMIZATION_ORDER[right.customization];
    if (customization !== 0) return customization;
    return left.name.localeCompare(right.name, 'fr');
  });
}

/** Keep the semantic selection across catalog refetches and source changes. */
export function keepActiveSkillSlug(
  skills: ResolvedSkillMetadata[],
  activeSlug: string | null,
): string | null {
  if (activeSlug !== null && skills.some((skill) => skill.slug === activeSlug)) {
    return activeSlug;
  }
  return skills[0]?.slug ?? null;
}

export function moveActiveSkillSlug(
  skills: ResolvedSkillMetadata[],
  activeSlug: string | null,
  direction: 1 | -1,
): string | null {
  if (skills.length === 0) return null;
  const currentIndex = Math.max(0, skills.findIndex((skill) => skill.slug === activeSlug));
  return skills[(currentIndex + direction + skills.length) % skills.length]?.slug ?? null;
}

export function hasSkillDraftChanged(
  entry: SkillSettingsEntry,
  draft: PersonalSkillDraft,
): boolean {
  return draft.slug !== entry.skill.slug
    || draft.description !== entry.skill.description
    || draft.content !== entry.skill.content;
}

export function replaceSlashToken(
  value: string,
  token: SlashToken,
  slug: string,
): string {
  const tail = value.slice(token.end);
  const separator = tail.length === 0 ? ' ' : '';
  return `${value.slice(0, token.start)}/${slug}${separator}${tail}`;
}
